import { getDashboardStatsAction, getFormDataListAction, getUpcomingEventsAction } from '../Action/modernAdminActions';
import { getDashboardStatisticsAction, getUpcomingEventAction, getMedpassPrescriptionListAction } from '../Action/admin.action';
import { decryptData } from '../component/admin/decrypt';
import propertise from '../config/properties';
import toast from 'react-hot-toast';
import apiClient from './apiClient'; // Import the modern API client

// Configuration for API retry logic
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  backoffMultiplier: 2
};

// Utility function for API retry logic
const withRetry = async (apiCall, retries = RETRY_CONFIG.maxRetries) => {
  try {
    return await apiCall();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, RETRY_CONFIG.retryDelay));
      return withRetry(apiCall, retries - 1);
    }
    throw error;
  }
};

// Utility function for consistent error handling
const handleApiError = (error, context = 'API call') => {
  console.error(`Error in ${context}:`, error);
  
  // Determine appropriate error message
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    if (status === 401) {
      errorMessage = 'Session expired. Please log in again.';
    } else if (status === 403) {
      errorMessage = 'You do not have permission to access this resource.';
    } else if (status === 404) {
      errorMessage = 'The requested resource was not found.';
    } else if (status >= 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    }
  } else if (error.request) {
    // Network error
    errorMessage = 'Network error. Please check your connection and try again.';
  }
  
  toast.error(errorMessage);
  return { success: false, error: errorMessage, originalError: error };
};

// Data transformation utilities
const transformDashboardStats = (data, useModernAPI = true) => {
  if (!data) return {};
  
  const statisticsData = useModernAPI ? (data.statistics || data || {}) : data;
  
  return {
    statistics: {
      totalPatients: statisticsData.totalPatients || 0,
      totalStaffs: statisticsData.totalStaffs || 0,
      totalForms: statisticsData.totalForms || 0,
      totalUserGroups: statisticsData.totalUserGroups || 0,
      activeForms: statisticsData.activeForms || 0,
      pendingReviews: statisticsData.pendingReviews || 0,
      totalUsers: statisticsData.totalUsers || 0,
      todayAppointments: statisticsData.todayAppointments || 0,
      totalMedications: statisticsData.totalMedications || 0
    },
    userRole: data.userRole || 0,
    daysSinceLastUpdate: data.daysSinceLastUpdate || 0
  };
};

const transformFormData = (data) => {
  if (!data) return { fields: [], records: [], totalRows: 0 };
  
  let fields = data.fields || [];
  let records = data.records || [];
  
  // Remove the element at index 2 if it exists (legacy requirement)
  if (Array.isArray(fields) && fields.length > 2) {
    fields = [...fields];
    fields.splice(2, 1);
  }
  
  return {
    fields,
    records,
    totalRows: data.totalRows || records.length || 0
  };
};

const transformPatientRecord = (record) => {
  if (!record) return null;
  
  // Handle both array and object formats
  if (Array.isArray(record)) {
    return {
      ...record,
      patientName: `${record[16] || 'N/A'} ${record[18] || ''}`.trim(),
      firstName: record[16] || '',
      lastName: record[18] || '',
      mobileNumber: record[30] || '',
      dateOfBirth: record[20] || '',
      emrNumber: record[15] || 'N/A',
      mrnNumber: record[14] || 'N/A',
      status: record[33] || 0,
      recordId: record[0] || record.id
    };
  } else {
    return {
      ...record,
      patientName: record.patientName || `${record.firstName || 'N/A'} ${record.lastName || ''}`.trim(),
      firstName: record.firstName || '',
      lastName: record.lastName || '',
      mobileNumber: record.mobileNumber || '',
      dateOfBirth: record.dateOfBirth || '',
      emrNumber: record.emrNumber || 'N/A',
      mrnNumber: record.mrnNumber || 'N/A',
      status: record.status || 0,
      recordId: record.recordId || record.id
    };
  }
};

const transformDashboardSummary = (data) => {
  if (!data) return null;

  const stats = data.statistics || {};
  const recentPatients = (data.recent_patients || []).map(transformPatientRecord);

  return {
    statistics: {
      totalPatients: stats.totalPatients || 0,
      totalStaffs: stats.totalStaffs || 0,
      totalForms: stats.totalForms || 0,
      totalUserGroups: stats.totalUserGroups || 0,
    },
    formData: recentPatients,
    totalRows: recentPatients.length,
    userRole: data.user_role || 0,
    daysSinceLastUpdate: data.days_since_last_update || 0,
  };
};

// Main API service class
class DashboardApiService {
  constructor() {
    this.useModernAPI = true; // Default to modern API
  }
  
  setApiMode(useModern) {
    this.useModernAPI = useModern;
  }
  
  async fetchDashboardSummary() {
    try {
      const response = await withRetry(() => apiClient.get('/dashboard/summary'));
      const transformedData = transformDashboardSummary(response.data);
      return { success: true, data: transformedData };
    } catch (error) {
      return handleApiError(error, 'fetching dashboard summary');
    }
  }

  async fetchStaffDashboardSummary() {
    try {
      const response = await withRetry(() => apiClient.get('/dashboard/staff-summary'));
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, 'fetching staff dashboard summary');
    }
  }

  async fetchMedpassPrescriptionList(mrnNumber) {
    try {
      const result = await withRetry(async () => {
        const response = await getMedpassPrescriptionListAction({ mrnNumber });
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch prescription list');
        }
        
        return response;
      });
      
      const prescriptionData = decryptData(result.data) || [];
      
      return {
        success: true,
        data: prescriptionData
      };
      
    } catch (error) {
      return handleApiError(error, 'fetching prescription list');
    }
  }
  
  async fetchPatientRecordDashboard(fromDate, toDate) {
    try {
      const result = await withRetry(async () => {
        const { getPatientRecordDashboardAction } = await import('../Action/admin.action');
        const response = await getPatientRecordDashboardAction({ fromDate, toDate });
        
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch patient record dashboard');
        }
        
        return response;
      });
      
      const dashboardData = decryptData(result.data) || {};
      
      return {
        success: true,
        data: {
          totalPatient: dashboardData.totalPatient || 0,
          dischargePatient: dashboardData.dischargePatient || 0,
          activePatient: dashboardData.activePatient || 0
        }
      };
      
    } catch (error) {
      return handleApiError(error, 'fetching patient record dashboard');
    }
  }
}

const dashboardApiService = new DashboardApiService();
export default dashboardApiService; 